/**
 * Detects the version of a translator configuration
 * Only V1 configs will not have a version field. V2 and beyond will be explicitly versioned.
 */
export function detectConfigVersion(config: TranslatorConfigConfig): ConfigVersion {
  // If version field exists, use it
  if (config.version) {
    return config.version === '2' ? '2' : '1'
  }

  // No version field means V1 (only V1 configs lack explicit version)
  return '1'
}

/**
 * Type guard to check if a schedule is a V1 Schedule
 */
export function isV1Schedule(schedule: Schedule | ScheduleGroup): schedule is Schedule {
  return !('schedules' in schedule)
}

/**
 * Gets a display-friendly version label
 */
export function getVersionLabel(version: ConfigVersion): string {
  return version === '2' ? 'Version 2 (Grouped Schedules)' : 'Version 1 (Legacy)'
}

/**
 * Validates a config for a specific version
 */
export function validateConfig(
  config: TranslatorConfigConfig,
  version: ConfigVersion
): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  if (version === '1') {
    // V1 validation
    Object.entries(config.schedules || {}).forEach(([name, schedule]) => {
      if (isV1Schedule(schedule as Schedule)) {
        if (!schedule.adapter) {
          errors.push(`Schedule '${name}' missing required field 'adapter'`)
        }
        if (!schedule.type) {
          errors.push(`Schedule '${name}' missing required field 'type'`)
        }
        if (!schedule.interval_minutes && !schedule.cron) {
          errors.push(`Schedule '${name}' must have either 'interval_minutes' or 'cron'`)
        }
      }
    })

    warnings.push(
      'V1 configuration format is supported but V2 is recommended for better schedule management'
    )
  } else if (version === '2') {
    // V2 validation
    if (config.schedules && Array.isArray(config.schedules)) {
      config.schedules.forEach((scheduleGroup: ScheduleGroup, groupIndex) => {
        if (!scheduleGroup.adapter) {
          errors.push(`Schedule group #${groupIndex} missing required field 'adapter'`)
        }
        if (!scheduleGroup.type) {
          errors.push(`Schedule group #${groupIndex} missing required field 'type'`)
        }
        if (!scheduleGroup.schedules || scheduleGroup.schedules.length === 0) {
          errors.push(`Schedule group #${groupIndex} must have at least one schedule`)
        } else {
          scheduleGroup.schedules.forEach((schedule, index) => {
            if (!schedule.interval_minutes && !schedule.cron) {
              errors.push(
                `Schedule group #${groupIndex} schedule #${index} must have either 'interval_minutes' or 'cron'`
              )
            }
          })
        }
      })
    } else {
      errors.push("V2 configuration must have a 'schedules' array of adapter groups")
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  }
}

export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}
